import { Locale, LocaleMessageType } from "@/types/locale";
import { useLocale } from "next-intl";
import { isInstanceOfLocalizeString, localizeFrom } from "@lib/i18n/utils";

/**
 * Component that handles localization for a given message or string.
 *
 * The `LocaleString` component takes in a `content` object which can be either a string
 * or an instance of `LocaleMessageType`. It localizes the content based on the current
 * locale if it is an instance of `LocaleMessageType`. Otherwise, it directly renders the
 * provided string content.
 *
 * @param {LocaleMessageType|string} content - The message or string to be localized/rendered.
 * @returns {string} Returns a localized string if the `content` is a `LocaleMessageType`. Otherwise,
 * it returns the original string content.
 */
export const LocaleString = ({
  content,
  defaultValue,
}: {
  content: LocaleMessageType | string;
  defaultValue?: string;
}): string => {
  const locale = useLocale() as Locale;

  if (isInstanceOfLocalizeString(content))
    return localizeFrom(content as LocaleMessageType, locale, defaultValue);

  return content as string;
};
